// View Application Page - Displays detailed application information in tabular form
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
    Breadcrumbs,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Container,
    Avatar,
    Stack
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Assignment as AssignmentIcon,
    History as HistoryIcon,
    Science as ScienceIcon,
     Business as BusinessIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import DoApiService from '../../services/DoApiService';
import { decryptAppId, decryptText, encryptAppId, encryptText } from '../../utils/encryption';

const ViewApplicationPage = () => {
    // State management
    const [applicationData, setApplicationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Router hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get encrypted parameters from URL (similar to .NET Session variables)
    const encryptedAppId = searchParams.get('appId');
    const encryptedOrgId = searchParams.get('orgId');
    const encryptedLabTypeId = searchParams.get('labTypeId');
    const encryptedLaboratoryName = searchParams.get('laboratoryName');

    // Decrypt parameters
    const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
    const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
    const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;
    const laboratoryName = encryptedLaboratoryName ? decryptText(encryptedLaboratoryName) : null;



    // Load application details
    const loadApplicationData = async () => {
        if (!appId || !orgId) {
            setError('Invalid application parameters. Please check the URL.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Get user data for API call
            const userData = JSON.parse(localStorage.getItem('userData') || '{}');

            // Load application details using mode 86 (equivalent to .NET rptrApps_DataBind)
            const response = await DoApiService.getViewApplicationData({
                orgId: orgId // Use orgId from URL parameters like .NET Session["OrgId"]
            });

            if (response.success && response.data && response.data.length > 0) {
                // Mode 86 returns an array, store all applications
                setApplicationData(response.data);

                // Store in session equivalent (localStorage) similar to .NET Session
                localStorage.setItem('ViewApp_AppId', appId);
                localStorage.setItem('ViewApp_OrgId', orgId);
                localStorage.setItem('ViewApp_LabTypeId', labTypeId || '');

            } else {
                throw new Error(response.message || 'No application data found for this organization');
            }

        } catch (err) {
            setError(`Error loading application: ${err.message}`);
            console.error('Load application error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load data when component mounts
    useEffect(() => {
        loadApplicationData();
    }, [appId, orgId]);

    // Format date for display
    const formatDate = (dateString) => {
        return DoApiService.formatDate(dateString, 'datetime');
    };

    // Handle back navigation
    const handleBack = () => {
        navigate(-1); // Go back to previous page
    };

    // Get assessment type display
    const getAssessmentTypeDisplay = (assessmentTypeId) => {
        const types = {
            1: { name: 'New Application', color: 'primary' },
            2: { name: 'Renewal', color: 'success' },
            3: { name: 'Extension', color: 'info' },
            4: { name: 'Surveillance', color: 'warning' },
            5: { name: 'Re-assessment', color: 'secondary' },
            6: { name: 'Special', color: 'error' },
            7: { name: 'Others', color: 'default' }
        };
        return types[assessmentTypeId] || { name: 'Unknown', color: 'default' };
    };

    // Get lab type display
    const getLabTypeDisplay = (labTypeId) => {
        const types = {
            1: 'Testing Laboratory',
            2: 'Calibration Laboratory',
            7: 'Medical Laboratory'
        };
        return types[labTypeId] || 'Unknown';
    };

    // Handle view history button click
    const handleViewHistory = (application) => {
        console.log('View History clicked for application:', application.appId);
        // Navigate to history page with encrypted parameters (similar to existing history functionality)
        const encryptedAppId = encryptAppId(application.appId.toString());
        navigate(`/main/do/applications/history?appId=${encryptedAppId}`);
    };

    // Handle view details button click
    const handleViewDetails = (application) => {
        console.log('View Details clicked for application:', application.appId);

        // Navigate to appdashboard with encrypted parameters
        const encryptedAppId = encryptAppId(application.appId.toString());
        const encryptedOrgId = encryptText(application.orgId.toString());
        const encryptedLabTypeId = encryptText(application.labTypeId?.toString() || '');

        // Navigate to appdashboard page with encrypted parameters
        navigate(`/main/view/applications/appdashboard?appId=${encryptedAppId}&orgId=${encryptedOrgId}&labTypeId=${encryptedLabTypeId}`);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading application details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
                    Go Back
                </Button>
            </Box>
        );
    }

    if (!applicationData || applicationData.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    No application data found
                </Typography>
                <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
       
            <Container maxWidth="xl">
                <Card 
                    sx={{ 
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', 
                        borderRadius: 3,
                        border: '1px solid rgba(0,0,0,0.08)',
                        overflow: 'hidden'
                    }}
                >
                    <CardContent sx={{ p: 0 }}>
                        {/* Table Header */}
                        <Box 
                            sx={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                p: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        mr: 2,
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <BusinessIcon />
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Application Details
                                </Typography>
                                
                            </Box>
                              <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={handleBack}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        },
                                    }}
                                >
                                    Back
                                </Button>
                            </Stack>
                        </Box>

            {/* Main Content - Simple Table */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 'medium'}}>
                                Application Information ({applicationData.length} records)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1rem', mb: 3 }}>
                               {laboratoryName}
                            </Typography>
                            {/* Enhanced Table */}
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AssignmentIcon fontSize="small" />
                                                    Lab ID
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <ScienceIcon fontSize="small" />
                                                    Application Type
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircleIcon fontSize="small" />
                                                    Status
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main'
                                                }}
                                            >
                                                Date Created
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main'
                                                }}
                                            >
                                                Date Submitted
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: 'primary.main',
                                                    py: 2.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                         {applicationData.map((application, index) => (
                                        <TableRow
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'rgba(103, 126, 234, 0.08)',
                                                    transform: 'scale(1.001)',
                                                    transition: 'all 0.2s ease'
                                                },
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }}
                                        >
                                            <TableCell sx={{ py: 3 }}>
                                                <Chip
                                                    label={application.ApplicationNo || application.applicationNo || 'N/A'}
                                                    color="primary"
                                                    variant="filled"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: '0.9rem',
                                                        px: 2,
                                                        py: 1
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 3 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                    {application.laboratoryName || application.assessmentType || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 3 }}>
                                                <Chip
                                                    label={application.Status || application.status || 'N/A'}
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        borderWidth: 2,
                                                        height: 'auto',
                                                        minHeight: '32px',
                                                        '& .MuiChip-label': {
                                                            whiteSpace: 'normal',
                                                            textOverflow: 'clip',
                                                            overflow: 'visible',
                                                            display: 'block',
                                                            padding: '8px 12px',
                                                            lineHeight: 1.2
                                                        },
                                                        '&:hover': {
                                                            transform: 'scale(1.05)'
                                                        },
                                                        transition: 'transform 0.2s ease'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 3 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                    {formatDate(application.applicationDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 3 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                    {formatDate(application.submitDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                border: '1px solid #e0e0e0',
                                                textAlign: 'center'
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexDirection: 'column' }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="info"
                                                        onClick={() => handleViewDetails(application)}
                                                        startIcon={<VisibilityIcon />}
                                                        sx={{ minWidth: '120px' }}
                                                    >
                                                        Dashboard
                                                    </Button>
                                                    {/* <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleViewHistory(application)}
                                                        startIcon={<HistoryIcon />}
                                                        sx={{ minWidth: '100px' }}
                                                    >
                                                        History
                                                    </Button> */}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </CardContent>
                </Card>
            </Container>
    );
};

export default ViewApplicationPage;
